import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, makeStyles } from '@material-ui/core';
import { Heading, Block, Typography, ActionLink, Loader } from '@klp/design';
import { Main } from '@klp/layout/Main';
import { showToast } from '@klpkonsern/ui';

import {
    ContractManagementRoutes,
    ContractManagementTabs,
} from '@forvalter/routing/Routes';
import { useParams, useHistory } from 'react-router-dom';
import { ConsultantSelector } from '../components/ConsultantSelector';
import {
    Activities,
    ConsultantRoles,
    RegistrationStatuses,
    useGetContractRegistrationProcessQuery,
    useGetContractToRegistrationQuery,
    useGetRegistrationStatusQuery,
} from '../services/rtkApi';
import type { IContractToRegistrationProcessActivity } from '../classes/ContractToRegistrationProcessResponse';
import { ContractSummary } from '../components/ContractSummary';

const useStyles = makeStyles((theme) => ({
    dokument: {
        width: '80%',
        marginTop: '10px',
        paddingTop: '2px',
        paddingBottom: '2px',
        paddingLeft: '20px',
        borderColor: theme.palette.primary.medium,
        borderRadius: 2,
        border: '1px solid transparent',
    },
    buttons: {
        width: '120px',
        paddingTop: '10px',
        paddingBottom: '10px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
    },
    consultantSelectorContainer: {
        width: '80%',
    },
    previewDownload: {
        cursor: 'pointer',
    },
}));

export default function AssignResponsibleView() {
    const { t } = useTranslation();
    const classes = useStyles();
    const { kontraktId } = useParams<{ kontraktId: string }>();
    const history = useHistory();

    const { data: registrationStatusData } = useGetRegistrationStatusQuery();

    const {
        data: contractRegistrationProcessData,
        isLoading: contractToRegistrationProcessLoading,
        isError: contractToRegistrationProcessError,
    } = useGetContractRegistrationProcessQuery({
        kontraktDokumentId: kontraktId,
        startNewProcess: false,
    });

    const {
        data: contractToRegistration,
        isLoading: contractToRegistrationLoading,
        isError: contractToRegistrationError,
    } = useGetContractToRegistrationQuery({
        kontraktId,
    });

    const registrationStatuses = registrationStatusData?.reduce(
        (acc, status) => ({
            ...acc,
            [status]: status,
        }),
        {} as { [key in keyof typeof RegistrationStatuses]: string },
    );

    const activities = contractRegistrationProcessData?.aktiviteter?.reduce(
        (acc, aktiviteter) => ({
            ...acc,
            [aktiviteter.taskDefinitionId]: aktiviteter,
        }),
        {} as {
            [key in keyof typeof Activities]: IContractToRegistrationProcessActivity;
        },
    );

    const handleComplete = () => {
        history.push(
            ContractManagementRoutes.tab(ContractManagementTabs.registrering),
        );

        showToast({
            body: t('contractRegistration.successfulOperation'),
            type: 'success',
        });
    };

    if (contractToRegistrationError || contractToRegistrationProcessError) {
        showToast({
            body: t('checkOffContract.error'),
            type: 'error',
        });
        history.push(
            ContractManagementRoutes.tab(ContractManagementTabs.registrering),
        );
    }

    const isLoading =
        contractToRegistrationProcessLoading || contractToRegistrationLoading;
    if (isLoading) {
        return <Loader isLoading={isLoading} />;
    }

    return (
        <Main>
            <Main.Breadcrumbs>
                <Main.Crumb
                    id="contractManagement"
                    label={t('breadcrumbs.contractManagement')}
                    href={ContractManagementRoutes.moduleRoute()}
                />
                <Main.Crumb
                    id="checkOffContract"
                    label={contractToRegistration?.navn}
                />
            </Main.Breadcrumbs>
            <Main.Navigation>
                <ActionLink
                    actionType="linkBack"
                    translation="checkOffContract.backButton"
                    href={ContractManagementRoutes.tab(
                        ContractManagementTabs.registrering,
                    )}
                />
            </Main.Navigation>
            {contractToRegistration && (
                <ContractSummary contract={contractToRegistration} />
            )}
            <Grid
                container={true}
                className={classes.consultantSelectorContainer}
                direction="column"
            >
                <Block paddingTop="compact">
                    <Heading
                        klpHeadingLevel="THREE"
                        klpColor="dark"
                        translation={`checkOffContract.step.${Activities.TILDEL_OKONOMI}.secondaryTitle`}
                    />
                </Block>
                <Block
                    paddingTop="tiny"
                    paddingBottom="tiny"
                    style={{ width: '60%' }}
                >
                    <Typography>
                        {t(
                            `checkOffContract.step.${Activities.TILDEL_OKONOMI}.descriptionPartOne`,
                        )}
                    </Typography>
                    <Typography>
                        {t(
                            `checkOffContract.step.${Activities.TILDEL_OKONOMI}.descriptionPartTwo`,
                        )}
                    </Typography>
                    <ConsultantSelector
                        status={
                            registrationStatuses && {
                                [RegistrationStatuses.TILDELT_OKONOMI]:
                                    RegistrationStatuses.TILDELT_OKONOMI,
                            }
                        }
                        activity={
                            activities && {
                                [Activities.TILDEL_OKONOMI]:
                                    activities[Activities.TILDEL_OKONOMI],
                            }
                        }
                        filter="økonomi"
                        completeTask={false}
                        completeStepCallback={handleComplete}
                        sortBy={ConsultantRoles.OKONOMI}
                    />
                </Block>
            </Grid>
        </Main>
    );
}
